import { useEffect, useRef, useState } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { ArrowLeft, ArrowRight, MessageSquareText, X } from "lucide-react";
import { imgSrcSet, imgUrl } from "@/lib/api";
import { useImageAlts } from "@/hooks/use-image-alts";
import { creditLabel } from "@/components/shared/PhotoCredit";
import { cn } from "@/lib/utils";
import { explicitFit } from "@/lib/display";
import { PhotoCommentDialog, PhotoNumberTag } from "@/components/shared/PhotoNumber";

const SWIPE_PX = 60;
const EASE = [0.16, 1, 0.3, 1];

const NavButton = ({ dir, onClick, testId, className }) => (
  <button type="button" onClick={onClick} aria-label={dir > 0 ? "Neste bilete" : "Førre bilete"} data-testid={testId}
    className={cn("flex h-12 w-12 items-center justify-center rounded-full border border-paper/30 bg-ink/40 text-paper backdrop-blur-md transition-[background-color,transform] duration-300 hover:scale-105 hover:bg-paper hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sol", className)}>
    {dir > 0 ? <ArrowRight className="h-5 w-5" /> : <ArrowLeft className="h-5 w-5" />}
  </button>
);

/**
 * Full-screen viewer. `index` is null when closed. Swipe, arrow keys, counter, caption and a thumbnail strip;
 * the neighbours are pre-loaded so stepping never shows an empty frame.
 */
export const Lightbox = ({ images, index, onChange, alt = "", captions = {}, display, testId = "lightbox" }) => {
  const open = index !== null && index !== undefined;
  const n = images.length;
  const reduce = useReducedMotion();
  const { altFor, creditFor, numberFor } = useImageAlts();
  const [dir, setDir] = useState(0);
  const [comment, setComment] = useState(false);
  const stripRef = useRef(null);
  const jump = (i, d) => { setDir(d); onChange(((i % n) + n) % n); };
  const step = (d) => jump(index + d, d);

  useEffect(() => {
    if (!open) return;
    [index + 1, index - 1].forEach((i) => { const s = images[((i % n) + n) % n]; if (s) { const im = new Image(); im.src = imgUrl(s, 1600); } });
  }, [open, index, images, n]);
  useEffect(() => {
    if (!open) return;
    stripRef.current?.querySelector(`[data-i="${index}"]`)?.scrollIntoView({ block: "nearest", inline: "center", behavior: reduce ? "auto" : "smooth" });
  }, [open, index, reduce]);

  const src = open ? images[index] : null;
  const number = src ? numberFor(src) : null;
  const caption = src ? captions[src] || altFor(src, "") : "";
  const credit = src ? creditLabel(creditFor(src)) : "";
  const variants = {
    enter: (d) => ({ opacity: 0, x: reduce ? 0 : d * 48, scale: reduce ? 1 : 0.98 }),
    center: { opacity: 1, x: 0, scale: 1 },
    exit: (d) => ({ opacity: 0, x: reduce ? 0 : d * -48, scale: reduce ? 1 : 0.98 }),
  };

  return (
    <DialogPrimitive.Root open={open} onOpenChange={(o) => !o && onChange(null)}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-[95] bg-ink/95 backdrop-blur-2xl data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <DialogPrimitive.Content
          className="fixed inset-0 z-[95] flex flex-col text-paper focus:outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
          onKeyDown={(e) => { if (comment || /^(INPUT|TEXTAREA)$/.test(e.target?.tagName || "")) return; if (e.key === "ArrowRight") step(1); if (e.key === "ArrowLeft") step(-1); }}
          data-testid={`${testId}-lightbox`}
        >
          <DialogPrimitive.Title className="sr-only">{alt || "Bilete"}</DialogPrimitive.Title>
          <DialogPrimitive.Description className="sr-only">Bla med piltastane eller sveip. Esc lukkar.</DialogPrimitive.Description>
          <header className="flex items-center justify-between gap-4 px-4 pt-3 sm:px-6 sm:pt-4">
            <span className="flex min-w-0 items-center gap-3">
              <span className="t-mono tabular whitespace-nowrap text-paper/70" data-testid={`${testId}-lightbox-counter`}>Bilete {open ? index + 1 : 0} av {n}</span>
              {number && <PhotoNumberTag number={number} tone="paper" className="whitespace-nowrap" testId={`${testId}-lightbox-number`} />}
            </span>
            <span className="flex items-center gap-2">
              {number && (
                <button type="button" onClick={() => setComment(true)} aria-label={`Kommenter bilete nr. ${number} – privat til administrasjonen`} className="inline-flex h-11 items-center gap-2 rounded-full border border-paper/30 px-4 text-[13px] font-medium transition-colors duration-300 hover:bg-paper hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sol" data-testid={`${testId}-lightbox-comment`}>
                  <MessageSquareText className="h-3.5 w-3.5" /> <span className="hidden sm:inline">Kommenter</span>
                </button>
              )}
              <DialogPrimitive.Close className="inline-flex h-11 items-center gap-2 rounded-full border border-paper/30 px-4 text-[13px] font-medium transition-colors duration-300 hover:bg-paper hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sol" data-testid={`${testId}-lightbox-close`}>Lukk <X className="h-3.5 w-3.5" /></DialogPrimitive.Close>
            </span>
          </header>
          {number && <PhotoCommentDialog src={src} number={number} open={comment} onOpenChange={setComment} testId={`${testId}-photo-comment`} />}

          <div className="relative flex min-h-0 flex-1 items-center justify-center px-3 py-3 sm:px-20">
            <AnimatePresence custom={dir} initial={false} mode="popLayout">
              {src && (
                <motion.img
                  key={`${src}-${index}`}
                  src={imgUrl(src, 2000)} srcSet={imgSrcSet(src)} sizes="100vw" alt={caption || alt} draggable={false}
                  custom={dir} variants={variants} initial="enter" animate="center" exit="exit" transition={{ duration: reduce ? 0.15 : 0.38, ease: EASE }}
                  drag={n > 1 ? "x" : false} dragConstraints={{ left: 0, right: 0 }} dragElastic={0.25}
                  onDragEnd={(_, info) => { if (info.offset.x < -SWIPE_PX || info.velocity.x < -500) step(1); else if (info.offset.x > SWIPE_PX || info.velocity.x > 500) step(-1); }}
                  className="max-h-full max-w-full select-none rounded-[8px] object-contain shadow-[0_30px_80px_-20px_rgba(0,0,0,.7)]"
                  data-testid={`${testId}-lightbox-image`}
                />
              )}
            </AnimatePresence>
            {n > 1 && (
              <>
                <NavButton dir={-1} onClick={() => step(-1)} testId={`${testId}-lightbox-prev`} className="absolute left-3 top-1/2 hidden -translate-y-1/2 sm:flex" />
                <NavButton dir={1} onClick={() => step(1)} testId={`${testId}-lightbox-next`} className="absolute right-3 top-1/2 hidden -translate-y-1/2 sm:flex" />
              </>
            )}
          </div>

          <footer className="px-4 pb-4 pt-2 sm:px-6 sm:pb-5">
            <div className="flex items-center justify-between gap-4 sm:justify-center">
              {n > 1 && <NavButton dir={-1} onClick={() => step(-1)} testId={`${testId}-lightbox-prev-mobile`} className="h-11 w-11 sm:hidden" />}
              <p className={cn("min-h-[20px] text-center text-[14px] text-paper/80", !caption && !credit && "sr-only")} data-testid={`${testId}-lightbox-caption`}>
                {caption}
                {credit && <span className="photo-credit !text-paper/55 sm:ml-3 sm:inline block sm:mt-0 mt-1" data-testid={`${testId}-lightbox-credit`}>{credit}</span>}
              </p>
              {n > 1 && <NavButton dir={1} onClick={() => step(1)} testId={`${testId}-lightbox-next-mobile`} className="h-11 w-11 sm:hidden" />}
            </div>
            {n > 1 && (
              <div ref={stripRef} className="mt-3 flex gap-2 overflow-x-auto pb-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" data-testid={`${testId}-lightbox-thumbs`}>
                {images.map((s, i) => (
                  <button key={`${s}-${i}`} type="button" data-i={i} onClick={() => jump(i, i > index ? 1 : -1)} aria-label={`Vis bilete ${i + 1}`} aria-current={i === index || undefined} data-testid={`${testId}-lightbox-thumb-${i}`}
                    className={cn("h-14 w-20 shrink-0 overflow-hidden rounded-[6px] ring-1 transition-[opacity,box-shadow] duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sol", i === index ? "opacity-100 ring-sol" : "opacity-45 ring-paper/10 hover:opacity-90")}>
                    <img src={imgUrl(s, 240)} alt="" loading="lazy" style={explicitFit(display, s).style} data-fit={explicitFit(display, s).fit} className={cn("h-full w-full", explicitFit(display, s).fit === "contain" ? "object-contain" : "object-cover")} />
                  </button>
                ))}
              </div>
            )}
          </footer>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
};
